import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import styles from '../MockParameters.module.css';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import InputGroup from 'react-bootstrap/InputGroup';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import RadioButton from 'components/buttons/RadioButton';
import { ProxyConfiguration } from 'types/rest/mockServiceTypes';
import CertificateSelector from 'components/certUploader/CertificateSelector';
import { useIsMount } from 'hooks/useIsMountHook';
import { REGEX_VALID_HTTP_URL } from 'utils/regexUtils';
import PasswordInput from 'components/forms/PasswordInput/PasswordInput';
import { Base64 } from 'js-base64';
import { getSelectedKeysFromAssetBundle, updateConfigurablePropertyInBundle } from 'utils/AssetBundleUtils';
import { AssetAssignmentBundle, SslConfig, SSlConfigMode } from '../SslConfiguration';

interface ProxyConfigProps {
  proxyConfig: ProxyConfiguration;
  onUpdateProxyConfig: (proxyConfig: ProxyConfiguration) => void;
  disabled: boolean;
}

const ProxyConfig = (props: ProxyConfigProps) => {
  const { t } = useTranslation();
  const [proxyConfig, setProxyConfig] = useState<ProxyConfiguration>(
    !_.isNil(props.proxyConfig) ? props.proxyConfig : { proxyUrl: null, username: null, password: null },
  );
  const [isValid, setIsValid] = useState(true);
  const isMount = useIsMount();

  useEffect(() => {
    if (!isMount && isValid) {
      props.onUpdateProxyConfig(proxyConfig);
    }
  }, [proxyConfig]);

  const proxyInput = useFormInput(
    _.get(proxyConfig, 'proxyUrl'),
    (fn: CallbackFn) => {
      if (fn.isValid) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
      setProxyConfig({ ...proxyConfig, proxyUrl: fn.value as string });
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: t('mockServices:errorMsgs.errorProxyCannotBeEmpty'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      value: REGEX_VALID_HTTP_URL,
      invalidMessage: t('mockServices:errorMsgs.errorProxyField'),
    },
  );

  const id = _.uniqueId();
  const isProxyEnabled = !_.isNil(_.get(proxyConfig, 'proxyUrl'));

  const getPassword = () => {
    if (_.isNil(proxyConfig.password) || _.isEmpty(proxyConfig.password)) {
      return null;
    }
    const pass = _.get(proxyConfig, 'password');
    return Base64.decode(pass);
  };

  /**
   * Builds bundle object based from proxy config.
   */
  const getBundle = () => {
    if (_.isNil(proxyConfig)) {
      return {
        assets: [],
        configKeyAssets: [],
      };
    }
    return {
      assets: !_.isNil(proxyConfig.assets) ? proxyConfig.assets : [],
      configKeyAssets: !_.isNil(proxyConfig.configKeyAssets) ? proxyConfig.configKeyAssets : [],
    };
  };

  const onSelectCertificateHandler = (certId: number) => {
    setProxyConfig({
      ...proxyConfig,
      assets: [{ assetId: certId, assetUsageType: 'CLIENT_TRUSTSTORE_CERT' }],
      configKeyAssets: [], // Reset config key asset
    });
  };

  const updateConfigPropertyValueHandler = (value: string) => {
    const assetBundle: AssetAssignmentBundle = {
      assets: proxyConfig.assets,
      configKeyAssets: proxyConfig.configKeyAssets,
    };
    const updatedBundle = updateConfigurablePropertyInBundle(assetBundle, value, 'CLIENT_TRUSTSTORE_CERT');
    setProxyConfig({
      ...proxyConfig,
      assets: updatedBundle.assets,
      configKeyAssets: updatedBundle.configKeyAssets,
    });
  };

  const assetKeysFromBundle = getSelectedKeysFromAssetBundle(getBundle(), SslConfig.ONE_WAY, SSlConfigMode.CLIENT);

  return (
    <Form.Group controlId={'qa-params-proxy-' + id}>
      {/* Proxy Disabled */}
      <div className={'mb-3'}>
        <RadioButton
          disabled={props.disabled}
          isChecked={!isProxyEnabled}
          label={t('mockServices:proxyConfig.disabled')}
          onChange={() => setProxyConfig(null)}
          id={'disable-enable-proxy'}
        />
      </div>

      {/* Proxy Enabled */}
      <div className={'mb-3'}>
        <RadioButton
          disabled={props.disabled}
          isChecked={isProxyEnabled}
          label={t('mockServices:proxyConfig.enabled')}
          onChange={() => setProxyConfig({ ...proxyConfig, proxyUrl: '' })}
          id={'enable-proxy'}
        />
        {isProxyEnabled && (
          <div className="ml-5">
            {/* Proxy */}
            <Form.Label>{t('mockServices:proxyConfig.label')}</Form.Label>
            <span className={styles.questionInTableHeader} title={t('common:showHelp')}>
              <HelpTooltip id={'live_system_endpoint_help'} />
            </span>
            <InputGroup className="mb-3">
              <BzFormControlWithHook
                disabled={props.disabled}
                useFormInputHook={proxyInput}
                placeholder="Enter proxy URL..."
                required={false}
              />
            </InputGroup>

            {/*Username*/}
            <div className="mb-3">
              <label htmlFor="proxyUsername" className="d-block">
                {t('common:forms.username')}
              </label>
              <Form.Control
                autoComplete={'off'}
                id={'proxyUsername'}
                placeholder={'Enter Username...'}
                value={proxyConfig.username}
                disabled={props.disabled}
                onChange={value => setProxyConfig({ ...proxyConfig, username: value.target.value })}
              />
            </div>

            {/*Password*/}
            <div className="mb-3">
              <label htmlFor="proxyPassword" className="d-block">
                {t('common:forms.password')}
              </label>

              <PasswordInput
                id={'proxyPassword'}
                disabled={props.disabled}
                onChange={value => {
                  const pass = Base64.encode(value);
                  setProxyConfig({ ...proxyConfig, password: pass });
                }}
                value={getPassword()}
              />
            </div>

            {/*Cert Selector*/}
            <CertificateSelector
              className={'mb-4'}
              type={'CERTIFICATE'}
              selectedCertificateId={assetKeysFromBundle.certificateId}
              onRemoveCertificate={() => setProxyConfig({ ...proxyConfig, assets: [], configKeyAssets: [] })}
              onSelectCertificate={onSelectCertificateHandler}
              onSelectAlias={_.noop}
              disabled={props.disabled}
              allowConfigurationProperty={true}
              onUpdateConfigPropertyValue={updateConfigPropertyValueHandler}
              configPropertyValue={assetKeysFromBundle.configKeyValue}
            />

            <label htmlFor="noProxy" className="d-block">
              {t('mockServices:proxyConfig.noProxy')}
            </label>
            <textarea
              disabled={props.disabled}
              placeholder={t('mockServices:proxyConfig.noProxyPlaceholder')}
              name="noProxy"
              className={'form-control'}
              rows={10}
              onChange={e => setProxyConfig({ ...proxyConfig, nonProxyHosts: e.target.value })}
              value={proxyConfig ? proxyConfig.nonProxyHosts : ''}
            />
          </div>
        )}
      </div>
    </Form.Group>
  );
};

export default ProxyConfig;
